import { atom } from "jotai";
import { atomFamily } from "jotai/utils";
import { EditCanvasResponseSchema } from "@zapier/service-providers";
import { z } from "zod";
import { abortCurrentStreamAtomFamily } from "../canvasAi";
import { emitAiPredictionUserFeedbackEventAtom } from "../events";
import { IMPLICIT_BAD } from "@zapier/canvas-constants";

export type AiSuggestedEdits = z.infer<typeof EditCanvasResponseSchema>;

const initialValue = null;

export const aiSuggestedEditsLoadingAtomFamily = atomFamily(
  (nodeId: string) => {
    const baseAtom = atom(false);
    baseAtom.debugLabel = `aiSuggestedEditsLoadingAtomFamily(${nodeId})`;
    return baseAtom;
  },
);

export const zslExecutionLoadingAtomFamily = atomFamily((nodeId: string) => {
  const baseAtom = atom(false);
  baseAtom.debugLabel = `zslExecutionLoadingAtomFamily(${nodeId})`;
  return baseAtom;
});

export const aiSuggestedEditsAtomFamily = atomFamily((nodeId: string) => {
  const baseAtom = atom<AiSuggestedEdits | null>(initialValue);
  baseAtom.debugLabel = `aiSuggestedEditsAtomFamily(${nodeId})`;
  return baseAtom;
});

export const cancelAiSuggestCommandAtomFamily = atomFamily((nodeId: string) => {
  const baseAtom = atom(null, (get, set) => {
    const aiSuggestedEdits = get(aiSuggestedEditsAtomFamily(nodeId));
    set(abortCurrentStreamAtomFamily(nodeId));
    set(aiSuggestedEditsAtomFamily(nodeId), null);
    set(aiSuggestedEditsLoadingAtomFamily(nodeId), false);
    set(zslExecutionErrorAtomFamily(nodeId), null);
    set(aiSuggestionErrorAtomFamily(nodeId), null);
    set(emitAiPredictionUserFeedbackEventAtom, {
      prediction_id: aiSuggestedEdits?.additions[0]?.id || "",
      user_rating: IMPLICIT_BAD,
      user_feedback: null,
      correlation_id: null,
    });
  });
  baseAtom.debugLabel = `cancelAiSuggestCommandAtomFamily(${nodeId})`;
  return baseAtom;
});

export const SUGGESTED_NODE_HEIGHT_FALLBACK = 300;

const suggestedNodeHeightsAtom = atom<number[]>([]);
suggestedNodeHeightsAtom.debugLabel = "suggestedNodeHeightsAtom";

export const suggestedNodeHeightsAtomFamily = atomFamily((nodeId: string) => {
  const baseAtom = atom(
    (get) => get(suggestedNodeHeightsAtom),
    (_get, set, heights: number[]) => set(suggestedNodeHeightsAtom, heights),
  );
  baseAtom.debugLabel = `suggestedNodeHeightsAtomFamily(${nodeId})`;
  return baseAtom;
});

export type ZslExecutionError = {
  message: string;
  type: "error" | "warning";
  zsl?: string;
  prompt?: string;
};

export type AiSuggestionError = {
  message: string;
  error?: unknown;
};

export const aiSuggestionErrorAtomFamily = atomFamily((nodeId: string) => {
  const baseAtom = atom<AiSuggestionError | null>(null);
  baseAtom.debugLabel = `aiSuggestionErrorAtomFamily(${nodeId})`;
  return baseAtom;
});

export const zslExecutionErrorAtomFamily = atomFamily((nodeId: string) => {
  const baseAtom = atom<ZslExecutionError | null>(null);
  baseAtom.debugLabel = `zslExecutionErrorAtomFamily(${nodeId})`;
  return baseAtom;
});
